import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  VideoCamera, Upload, Trash, Play, Eye, Clock, Plus, ListBullets,
  CaretDown, FilmStrip, ShoppingCart, ChartLineUp
} from '@phosphor-icons/react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function VideoLibrary() {
  const [videos, setVideos] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [tab, setTab] = useState('library');
  const [typeFilter, setTypeFilter] = useState('');
  const fileRef = useRef(null);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', video_type: 'long_form', category: 'Other', visibility: 'public' });
  const [showUpload, setShowUpload] = useState(false);
  const [newPlaylist, setNewPlaylist] = useState({ name: '', description: '' });
  const [showNewPl, setShowNewPl] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [overlayForm, setOverlayForm] = useState({ type: 'product_pin', label: '', offer_id: '', timestamp_start: 0 });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = typeFilter ? `?video_type=${typeFilter}` : '';
      const [vidRes, plRes, anaRes] = await Promise.all([
        api.get(`/videos/library${params}`),
        api.get('/videos/playlists/list'),
        api.get('/videos/analytics/summary'),
      ]);
      setVideos(vidRes.data || []);
      setPlaylists(plRes.data || []);
      setAnalytics(anaRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Select a video file'); return; }
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', uploadForm.title || file.name);
      formData.append('description', uploadForm.description);
      formData.append('video_type', uploadForm.video_type);
      formData.append('category', uploadForm.category);
      formData.append('visibility', uploadForm.visibility);
      await api.post('/videos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100)); }
      });
      toast.success('Video uploaded');
      setShowUpload(false);
      setUploadForm({ title: '', description: '', video_type: 'long_form', category: 'Other', visibility: 'public' });
      if (fileRef.current) fileRef.current.value = '';
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const deleteVideo = async (id) => {
    try {
      await api.delete(`/videos/${id}`);
      toast.success('Video deleted');
      fetchData();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const createPlaylist = async () => {
    if (!newPlaylist.name) return;
    try {
      await api.post('/videos/playlists/create', newPlaylist);
      toast.success('Playlist created');
      setShowNewPl(false);
      setNewPlaylist({ name: '', description: '' });
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const deletePlaylist = async (id) => {
    try {
      await api.delete(`/videos/playlists/${id}`);
      toast.success('Playlist deleted');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const addOverlay = async () => {
    if (!selectedVideo || !overlayForm.label) return;
    try {
      await api.post(`/videos/${selectedVideo}/overlays`, overlayForm);
      toast.success('Commerce overlay added');
      setSelectedVideo(null);
      setOverlayForm({ type: 'product_pin', label: '', offer_id: '', timestamp_start: 0 });
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const formatSize = (bytes) => {
    if (bytes > 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
    if (bytes > 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  };

  return (
    <div className="space-y-6" data-testid="video-library-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Video Library</h1>
          <p className="text-sm text-zinc-500 mt-1">Upload, manage, and monetize your video content</p>
        </div>
        <Button onClick={() => setShowUpload(true)} data-testid="upload-video-btn" className="bg-blue-600 hover:bg-blue-700">
          <Upload size={16} className="mr-2" /> Upload Video
        </Button>
      </div>

      {/* Analytics */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Videos', value: analytics.total_videos },
            { label: 'Short-form', value: analytics.short_form },
            { label: 'Long-form', value: analytics.long_form },
            { label: 'Total Views', value: analytics.total_views?.toLocaleString() },
            { label: 'Avg Views', value: analytics.avg_views },
          ].map((s, i) => (
            <Card key={i} className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-3">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.label}</p>
                <p className="text-lg font-bold mt-0.5">{s.value || 0}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800 pb-px">
        {[
          { key: 'library', label: 'Library', icon: FilmStrip },
          { key: 'playlists', label: 'Playlists', icon: ListBullets },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 transition-colors ${tab === t.key ? 'border-blue-500 text-blue-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader className="pb-3"><CardTitle className="text-base">Upload Video</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <input ref={fileRef} type="file" accept="video/*" data-testid="video-file-input"
              className="w-full text-sm text-zinc-400 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-blue-600 file:text-white file:text-sm file:cursor-pointer hover:file:bg-blue-700" />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="video-title" value={uploadForm.title} onChange={(e) => setUploadForm(p => ({...p, title: e.target.value}))} placeholder="Title" className="bg-zinc-800 border-zinc-700" />
              <div className="relative">
                <select data-testid="video-type-select" value={uploadForm.video_type} onChange={(e) => setUploadForm(p => ({...p, video_type: e.target.value}))}
                  className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer">
                  <option value="long_form">Long-form</option>
                  <option value="short_form">Short-form</option>
                </select>
                <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              </div>
            </div>
            <Input value={uploadForm.description} onChange={(e) => setUploadForm(p => ({...p, description: e.target.value}))} placeholder="Description" className="bg-zinc-800 border-zinc-700" />
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <select value={uploadForm.category} onChange={(e) => setUploadForm(p => ({...p, category: e.target.value}))}
                  className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer">
                  {["Review","Tutorial","Unboxing","Comparison","Vlog","Live Replay","Promo","Other"].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              </div>
              <div className="relative">
                <select value={uploadForm.visibility} onChange={(e) => setUploadForm(p => ({...p, visibility: e.target.value}))}
                  className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer">
                  <option value="public">Public</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="private">Private</option>
                </select>
                <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              </div>
            </div>
            {uploading && (
              <div className="space-y-1">
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 transition-all duration-300 rounded-full" style={{ width: `${uploadProgress}%` }} />
                </div>
                <p className="text-xs text-zinc-500 text-center">{uploadProgress}% uploaded</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={handleUpload} disabled={uploading} data-testid="confirm-upload-btn" className="bg-blue-600 hover:bg-blue-700">
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
              <Button variant="ghost" onClick={() => setShowUpload(false)} className="text-zinc-400">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Library Tab */}
      {tab === 'library' && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="relative w-40">
              <select data-testid="filter-video-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer">
                <option value="">All Types</option>
                <option value="short_form">Short-form</option>
                <option value="long_form">Long-form</option>
              </select>
              <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            </div>
          </div>
          {loading ? (
            <div className="text-center text-zinc-500 py-8">Loading videos...</div>
          ) : videos.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-8 text-center">
                <VideoCamera size={40} className="mx-auto text-zinc-600 mb-2" />
                <p className="text-zinc-500">No videos yet. Upload your first video to get started.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {videos.map(vid => (
                <Card key={vid.video_id} className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors overflow-hidden" data-testid={`video-${vid.video_id}`}>
                  <div className="relative bg-zinc-800 aspect-video flex items-center justify-center">
                    <video src={`${API_BASE}/api/videos/stream/${vid.video_id}`} className="w-full h-full object-cover" preload="metadata" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Play size={32} weight="fill" className="text-white/80" />
                    </div>
                    <Badge className={`absolute top-2 left-2 text-[10px] ${vid.type === 'short_form' ? 'bg-purple-900/60 text-purple-300' : 'bg-blue-900/60 text-blue-300'}`}>
                      {vid.type === 'short_form' ? 'Short' : 'Long'}
                    </Badge>
                    {vid.commerce_overlays?.length > 0 && (
                      <Badge className="absolute top-2 right-2 text-[10px] bg-amber-900/60 text-amber-300">
                        <ShoppingCart size={10} className="mr-0.5" /> {vid.commerce_overlays.length}
                      </Badge>
                    )}
                  </div>
                  <CardContent className="p-3">
                    <h3 className="text-sm font-medium text-zinc-200 truncate">{vid.title}</h3>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-500">
                      <span><Eye size={10} className="inline mr-0.5" />{vid.views || 0}</span>
                      <span>{vid.category}</span>
                      <span>{formatSize(vid.file_size || 0)}</span>
                      <Badge variant="outline" className={`text-[10px] ml-auto ${vid.visibility === 'public' ? 'text-emerald-400 border-emerald-700/40' : 'text-zinc-500 border-zinc-700'}`}>
                        {vid.visibility}
                      </Badge>
                    </div>
                    <div className="flex gap-1 mt-2">
                      <Button size="sm" variant="ghost" onClick={() => setSelectedVideo(vid.video_id)} className="h-7 px-2 text-xs text-amber-400 hover:text-amber-300" data-testid={`overlay-btn-${vid.video_id}`}>
                        <ShoppingCart size={12} className="mr-1" /> Overlay
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteVideo(vid.video_id)} className="h-7 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/20 ml-auto">
                        <Trash size={12} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Playlists Tab */}
      {tab === 'playlists' && (
        <div>
          <div className="flex justify-end mb-4">
            <Button size="sm" variant="ghost" onClick={() => setShowNewPl(true)} data-testid="new-playlist-btn" className="text-blue-400">
              <Plus size={16} className="mr-1" /> New Playlist
            </Button>
          </div>
          {showNewPl && (
            <Card className="mb-4 bg-zinc-900 border-zinc-700">
              <CardContent className="p-4 space-y-3">
                <Input data-testid="playlist-name" value={newPlaylist.name} onChange={(e) => setNewPlaylist(p => ({...p, name: e.target.value}))} placeholder="Playlist name" className="bg-zinc-800 border-zinc-700" />
                <Input value={newPlaylist.description} onChange={(e) => setNewPlaylist(p => ({...p, description: e.target.value}))} placeholder="Description" className="bg-zinc-800 border-zinc-700" />
                <div className="flex gap-2">
                  <Button size="sm" onClick={createPlaylist} data-testid="save-playlist-btn" className="bg-blue-600 hover:bg-blue-700">Create</Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowNewPl(false)} className="text-zinc-400">Cancel</Button>
                </div>
              </CardContent>
            </Card>
          )}
          {playlists.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-8">No playlists yet.</p>
          ) : (
            <div className="space-y-2">
              {playlists.map(pl => (
                <div key={pl.playlist_id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30 border border-zinc-800">
                  <div>
                    <p className="text-sm font-medium text-zinc-200">{pl.name}</p>
                    <p className="text-xs text-zinc-500">{pl.video_count || 0} videos &middot; {pl.visibility}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => deletePlaylist(pl.playlist_id)} className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/20">
                    <Trash size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Commerce Overlay Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <Card className="bg-zinc-900 border-zinc-700 w-full max-w-md mx-4" data-testid="overlay-modal">
            <CardHeader><CardTitle className="text-lg">Add Commerce Overlay</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Input data-testid="overlay-label" value={overlayForm.label} onChange={(e) => setOverlayForm(p => ({...p, label: e.target.value}))} placeholder="Product label (e.g. 'Buy Now')" className="bg-zinc-800 border-zinc-700" />
              <Input value={overlayForm.offer_id} onChange={(e) => setOverlayForm(p => ({...p, offer_id: e.target.value}))} placeholder="Offer ID (optional)" className="bg-zinc-800 border-zinc-700" />
              <Input type="number" value={overlayForm.timestamp_start} onChange={(e) => setOverlayForm(p => ({...p, timestamp_start: parseInt(e.target.value) || 0}))} placeholder="Show at (seconds)" className="bg-zinc-800 border-zinc-700" />
              <div className="flex gap-2">
                <Button onClick={addOverlay} data-testid="save-overlay-btn" className="bg-amber-600 hover:bg-amber-700">Add Overlay</Button>
                <Button variant="ghost" onClick={() => setSelectedVideo(null)} className="text-zinc-400">Cancel</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
